import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, Play, Expand, X } from 'lucide-react';
import { Button } from '../ui/button';
import PanoramaViewer from './PanoramaViewer';

// Demo tour data with AI-generated images
export const DEMO_TOURS = {
  'prop-001': {
    propertyTitle: 'Palm Jumeirah Luxury Villa',
    rooms: [
      {
        id: 'living',
        name: 'Grand Living Room',
        url: 'https://static.prod-images.emergentagent.com/jobs/bd752833-7911-44ec-acf6-47efa6f541ae/images/1cf58d50c296f6cede3e912714e5dddcb2e64578840716c2b384235be647a15f.png',
        description: 'Expansive living area with floor-to-ceiling windows offering panoramic Dubai skyline views.',
        highlights: ['Smart Home', 'Italian Marble', 'Custom Lighting']
      },
      {
        id: 'bedroom',
        name: 'Master Suite',
        url: 'https://static.prod-images.emergentagent.com/jobs/bd752833-7911-44ec-acf6-47efa6f541ae/images/efa5f92caca2d7a05637aa9d8c278910cb6e33ca3a2032288b734efc1122c0e4.png',
        description: 'Luxurious master bedroom with walk-in closet and spa-inspired ensuite bathroom.',
        highlights: ['Sea View', 'Walk-in Closet', 'Ensuite Spa']
      },
      {
        id: 'terrace',
        name: 'Rooftop Pool Terrace',
        url: 'https://static.prod-images.emergentagent.com/jobs/bd752833-7911-44ec-acf6-47efa6f541ae/images/57e500269b29ac634110fca700eb9f6e95c186c8d1467906025e341d4ff9be7d.png',
        description: 'Private infinity pool overlooking the Arabian Gulf with outdoor lounge and entertainment area.',
        highlights: ['Infinity Pool', 'Burj View', 'Outdoor Kitchen']
      }
    ]
  },
  'prop-002': {
    propertyTitle: 'Downtown Dubai Penthouse',
    rooms: [
      {
        id: 'living',
        name: 'Skyline Living Room',
        url: 'https://static.prod-images.emergentagent.com/jobs/bd752833-7911-44ec-acf6-47efa6f541ae/images/1cf58d50c296f6cede3e912714e5dddcb2e64578840716c2b384235be647a15f.png',
        description: 'Double-height living space with direct Burj Khalifa views and designer furnishings.',
        highlights: ['Burj View', 'Double Height', 'B&O Sound']
      },
      {
        id: 'bedroom',
        name: 'Master Bedroom',
        url: 'https://static.prod-images.emergentagent.com/jobs/bd752833-7911-44ec-acf6-47efa6f541ae/images/efa5f92caca2d7a05637aa9d8c278910cb6e33ca3a2032288b734efc1122c0e4.png',
        description: 'Serene master retreat with panoramic city views and bespoke Italian wardrobes.',
        highlights: ['City Panorama', 'Smart Blinds', 'Mood Lighting']
      }
    ]
  }
};

// Virtual Tour Button Component (for property cards)
export const VirtualTourButton = ({ propertyId, variant = 'icon' }) => {
  const [showTour, setShowTour] = useState(false);
  const tourData = DEMO_TOURS[propertyId];

  if (!tourData) return null;

  return (
    <>
      {variant === 'icon' ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowTour(true);
          }}
          className="absolute top-3 left-3 z-10 w-10 h-10 rounded-full bg-black/60 hover:bg-gold flex items-center justify-center transition-all group"
          title="Virtual Tour Available"
          data-testid="virtual-tour-btn"
        >
          <Eye className="w-5 h-5 text-white group-hover:text-black" />
        </button>
      ) : (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            setShowTour(true);
          }}
          className="bg-gradient-to-r from-gold to-amber-500 text-black hover:from-gold-light hover:to-amber-400"
          data-testid="virtual-tour-btn"
        >
          <Play className="w-4 h-4 mr-2" /> Virtual Tour
        </Button>
      )}

      {/* Tour Modal */}
      {showTour && (
        <div className="fixed inset-0 z-[100] bg-black">
          <PanoramaViewer
            images={tourData.rooms}
            propertyTitle={tourData.propertyTitle}
            isFullscreen={true}
            onClose={() => setShowTour(false)}
          />
        </div>
      )}
    </>
  );
};

// Virtual Tour Preview Card (for property detail page)
export const VirtualTourPreview = ({ propertyId }) => {
  const [showFullTour, setShowFullTour] = useState(false);
  const tourData = DEMO_TOURS[propertyId];

  if (!tourData) {
    return (
      <div className="glass-card p-6 text-center">
        <Eye className="w-12 h-12 text-text-muted mx-auto mb-3" />
        <h3 className="font-semibold mb-2">Virtual Tour</h3>
        <p className="text-text-secondary text-sm">Coming soon for this property</p>
      </div>
    );
  }

  return (
    <>
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card overflow-hidden"
      >
        {/* Preview Header */}
        <div className="relative aspect-video cursor-pointer group" onClick={() => setShowFullTour(true)}>
          <img 
            src={tourData.rooms[0].url}
            alt="Virtual Tour Preview"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          
          {/* Play Button Overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div 
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              className="w-20 h-20 rounded-full bg-gold/90 flex items-center justify-center cursor-pointer shadow-lg shadow-gold/30"
            >
              <Play className="w-8 h-8 text-black ml-1" />
            </motion.div>
          </div>

          {/* Room Count Badge */}
          <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-2">
            <Eye className="w-4 h-4 text-gold" />
            <span className="text-white text-sm font-medium">{tourData.rooms.length} Rooms</span>
          </div>

          {/* Title Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <h3 className="font-playfair text-xl font-semibold text-white">360° Virtual Tour</h3>
            <p className="text-white/70 text-sm">Explore every room in immersive detail</p>
          </div>
        </div>

        {/* Room Thumbnails */}
        <div className="p-4 bg-surface/50">
          <div className="grid grid-cols-3 gap-2">
            {tourData.rooms.map((room, idx) => (
              <button
                key={room.id}
                onClick={() => setShowFullTour(true)}
                className="relative aspect-[4/3] rounded-lg overflow-hidden group/thumb"
              >
                <img 
                  src={room.url}
                  alt={room.name}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover/thumb:scale-110"
                />
                <div className="absolute inset-0 bg-black/40 group-hover/thumb:bg-black/20 transition-colors" />
                <span className="absolute bottom-1 left-1 right-1 text-white text-xs truncate">
                  {room.name}
                </span>
              </button>
            ))}
          </div>
          
          <Button
            onClick={() => setShowFullTour(true)}
            className="w-full mt-4 bg-gold text-black hover:bg-gold-light"
            data-testid="start-virtual-tour-btn"
          >
            <Expand className="w-4 h-4 mr-2" /> Start Full Tour
          </Button>
        </div>
      </motion.div>

      {/* Full Tour Modal */}
      {showFullTour && (
        <div className="fixed inset-0 z-[100] bg-black">
          <PanoramaViewer
            images={tourData.rooms}
            propertyTitle={tourData.propertyTitle}
            isFullscreen={true}
            onClose={() => setShowFullTour(false)}
          />
        </div>
      )}
    </>
  );
};

export default { PanoramaViewer, VirtualTourButton, VirtualTourPreview, DEMO_TOURS };
